import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERVAL = 6500;

const Stars = ({ n }) => (
  <span className="inline-flex gap-1" aria-label={`${n} / 5`} data-testid="testimonial-rating">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-current" : ""}`} strokeWidth={1.6} />
    ))}
  </span>
);

export const Testimonials = () => {
  const { t } = useLang();
  const c = t.brand.testimonials;
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), INTERVAL);
    return () => clearInterval(id);
  }, [paused, items.length]);

  if (!items.length) return null;
  const cur = items[idx % items.length];
  const go = (d) => setIdx((i) => (i + d + items.length) % items.length);

  return (
    <section
      id="testimonials"
      className="relative py-16 sm:py-20 bg-[var(--page)] border-t border-[var(--hairline)]"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-3">
          <span className="text-xs uppercase tracking-[0.25em] text-terra font-semibold">{c.overline}</span>
          <h2 className="mt-4 font-display text-[var(--ink)] font-semibold tracking-tight leading-tight text-2xl sm:text-3xl">{c.title}</h2>
          {items.length > 1 && (
            <div className="mt-6 flex items-center gap-3">
              <button onClick={() => go(-1)} aria-label="Previous" data-testid="testimonial-prev" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-[var(--hairline)] hover:bg-[var(--ink)]/5 transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => go(1)} aria-label="Next" data-testid="testimonial-next" className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-[var(--hairline)] hover:bg-[var(--ink)]/5 transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
              <span className="text-xs text-[var(--muted-ink)] tabular-nums" data-testid="testimonial-counter">{idx + 1} / {items.length}</span>
            </div>
          )}
        </div>

        <div className="lg:col-span-9 relative min-h-[180px]">
          <Quote className="absolute -top-2 -left-1 h-10 w-10 text-[var(--ink)]/10" strokeWidth={1.2} />
          <AnimatePresence mode="wait">
            <motion.figure
              key={cur.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="pl-8 sm:pl-12"
              data-testid="testimonial-card"
            >
              <Stars n={cur.rating} />
              <blockquote className="mt-4 font-display text-xl sm:text-2xl lg:text-3xl leading-snug text-[var(--ink)]" data-testid="testimonial-quote">
                “{cur.quote}”
              </blockquote>
              <figcaption className="mt-5 text-sm text-[var(--muted-ink)]">
                <span className="font-semibold text-[var(--ink)]" data-testid="testimonial-name">{cur.name}</span>
                {cur.business && <span data-testid="testimonial-business"> · {cur.business}</span>}
              </figcaption>
            </motion.figure>
          </AnimatePresence>
          {items.length > 1 && (
            <div className="mt-6 pl-8 sm:pl-12 flex gap-2">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  onClick={() => setIdx(i)}
                  aria-label={`${i + 1}`}
                  className={`h-1 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-[var(--ink)]" : "w-3 bg-[var(--ink)]/20"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
